import { useMemo, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import SportsOutlinedIcon from '@mui/icons-material/SportsOutlined';
import {
  PLAYER_MAX,
  PLAYER_MIN,
  TEAM_MEMBER_MAX,
  computeGroupSizes,
  planBracket,
  playerKey,
  roundRobinMatchCount,
} from '../domain';
import type { Player, Referee } from '../domain';
import { useTournament } from '../state/TournamentContext';
import { useTranslation } from '../state/I18nContext';

interface PlayersViewProps {
  onStarted: () => void;
}

export function PlayersView({ onStarted }: PlayersViewProps) {
  const { tournament } = useTournament();
  if (!tournament) return null;
  const isFootball = tournament.sportId === 'FOOTBALL';
  return (
    <Box>
      {tournament.status === 'DRAFT' ? (
        <DraftRoster onStarted={onStarted} />
      ) : (
        <RosterTable />
      )}
      {/* Football carries a referee pool, manageable until the tournament is
          locked; the printed match-report forms draw their names from here. */}
      {isFootball && tournament.status !== 'FINISHED' && <RefereesCard />}
    </Box>
  );
}

/** Editable roster shown while the tournament is a DRAFT. */
function DraftRoster({ onStarted }: PlayersViewProps) {
  const { tournament, addPlayer, removePlayer, start } = useTournament();
  const { t } = useTranslation();
  const [name, setName] = useState('');
  // Football: the team whose member list is being edited (dialog open while set).
  const [editingTeam, setEditingTeam] = useState<Player | null>(null);
  const isFootball = tournament?.sportId === 'FOOTBALL';

  const players = useMemo<Player[]>(
    () =>
      tournament
        ? Object.values(tournament.players).sort((a, b) => a.seed - b.seed)
        : [],
    [tournament],
  );

  const count = players.length;
  const canAdd = count < PLAYER_MAX;
  const canStart = count >= PLAYER_MIN && count <= PLAYER_MAX;

  const preview = useMemo(() => {
    if (count < PLAYER_MIN) return null;
    const sizes = computeGroupSizes(count);
    const totalMatches = sizes.reduce(
      (sum, size) => sum + roundRobinMatchCount(size),
      0,
    );
    return { sizes, totalMatches, plan: planBracket(sizes.length) };
  }, [count]);

  const submitAdd = async () => {
    if (!canAdd) return;
    await addPlayer({ name, playerKey: playerKey(crypto.randomUUID()) });
    setName('');
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {isFootball
            ? t('admin.players.teamsTitle')
            : t('admin.players.rosterTitle')}
        </Typography>
        <Chip
          variant="outlined"
          label={
            isFootball
              ? t('admin.players.teamCount', { count })
              : t('admin.players.count', { count })
          }
        />
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          disabled={!canStart}
          onClick={() => void start().then(onStarted)}
        >
          {t('admin.players.startTournament')}
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label={
              isFootball
                ? t('admin.players.teamNameLabel')
                : t('admin.players.nameLabel')
            }
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submitAdd();
            }}
            disabled={!canAdd}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => void submitAdd()}
            disabled={!canAdd}
          >
            {t('admin.common.add')}
          </Button>
        </Stack>

        {count === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('admin.players.empty', { min: PLAYER_MIN })}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {players.map((player) => (
              <Stack
                key={player.id}
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ width: 36, flexShrink: 0 }}
                >
                  {player.id}
                </Typography>
                <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                  {player.name}
                </Typography>
                {isFootball && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<GroupsOutlinedIcon fontSize="small" />}
                    onClick={() => setEditingTeam(player)}
                  >
                    {t('admin.players.members.button', {
                      count: player.members.length,
                    })}
                  </Button>
                )}
                <IconButton
                  aria-label={t('admin.players.removeAria', { id: player.id })}
                  size="small"
                  onClick={() => void removePlayer(player.id)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          {isFootball
            ? t('admin.players.teamNamesHint')
            : t('admin.players.namesHint')}
        </Typography>
        {!canStart && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
            {t('admin.players.needRange', { min: PLAYER_MIN, max: PLAYER_MAX })}
          </Typography>
        )}
      </Paper>

      {preview && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('admin.players.preview.title')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {preview.sizes.map((size, index) => (
              <Chip
                key={index}
                label={t('admin.players.preview.groupChip', {
                  label: String.fromCharCode(65 + index),
                  size,
                })}
                color={size === 4 ? 'primary' : 'secondary'}
                variant="outlined"
              />
            ))}
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
            <Stat label={t('admin.players.preview.groups')} value={preview.sizes.length} />
            <Stat label={t('admin.players.preview.groupMatches')} value={preview.totalMatches} />
            <Stat label={t('admin.players.preview.advancing')} value={preview.plan.advancing} />
            <Stat label={t('admin.players.preview.bracketSize')} value={preview.plan.mainBracketSize} />
            <Stat label={t('admin.players.preview.byes')} value={preview.plan.byes} />
            <Stat label={t('admin.players.preview.playInMatches')} value={preview.plan.playInMatches} />
          </Box>
        </Paper>
      )}

      {editingTeam && (
        <TeamMembersDialog
          team={editingTeam}
          onClose={() => setEditingTeam(null)}
        />
      )}
    </Box>
  );
}

/**
 * Editor for a football team's squad member list (DRAFT only). One name per
 * line — the server trims entries, drops blanks and caps the list, so the
 * textarea can stay a forgiving free-text surface.
 */
function TeamMembersDialog({
  team,
  onClose,
}: {
  team: Player;
  onClose: () => void;
}) {
  const { updatePlayer } = useTournament();
  const { t } = useTranslation();
  const [text, setText] = useState(team.members.join('\n'));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await updatePlayer(team.id, { members: text.split('\n') });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="xs" onClose={() => !busy && onClose()}>
      <DialogTitle>
        {t('admin.players.members.dialogTitle', { name: team.name })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t('admin.players.members.dialogHint', { max: TEAM_MEMBER_MAX })}
        </DialogContentText>
        <TextField
          fullWidth
          multiline
          minRows={8}
          maxRows={16}
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('admin.players.members.placeholder')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('admin.common.cancel')}
        </Button>
        <Button variant="contained" onClick={() => void save()} disabled={busy}>
          {t('admin.common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The football tournament's referee pool: add and remove referees by name.
 * Available while DRAFT or IN_PROGRESS; the printed match-report forms and the
 * per-match assignment pickers draw from this list.
 */
function RefereesCard() {
  const { tournament, addReferee, removeReferee } = useTournament();
  const { t } = useTranslation();
  const [name, setName] = useState('');

  const referees = useMemo<Referee[]>(
    () =>
      tournament
        ? Object.values(tournament.referees).sort((a, b) => a.seed - b.seed)
        : [],
    [tournament],
  );

  const submitAdd = async () => {
    if (!name.trim()) return;
    await addReferee(name.trim());
    setName('');
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <SportsOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {t('admin.referees.title')}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={t('admin.referees.count', { count: referees.length })}
        />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label={t('admin.referees.nameLabel')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submitAdd();
          }}
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => void submitAdd()}
          disabled={!name.trim()}
        >
          {t('admin.common.add')}
        </Button>
      </Stack>

      {referees.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('admin.referees.empty')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {referees.map((referee) => (
            <Stack key={referee.id} direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ width: 88, flexShrink: 0 }}
              >
                {referee.id}
              </Typography>
              <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                {referee.name}
              </Typography>
              <IconButton
                aria-label={t('admin.referees.removeAria', { name: referee.name })}
                size="small"
                onClick={() => void removeReferee(referee.id)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {t('admin.referees.hint')}
      </Typography>
    </Paper>
  );
}

/** Read-only roster shown once the tournament has started. */
function RosterTable() {
  const { tournament, withdrawPlayer } = useTournament();
  const { t } = useTranslation();
  // Player awaiting withdrawal confirmation; the dialog is open while non-null.
  const [pending, setPending] = useState<Player | null>(null);
  const [busy, setBusy] = useState(false);

  const players = useMemo<Player[]>(
    () =>
      tournament
        ? Object.values(tournament.players).sort((a, b) => a.seed - b.seed)
        : [],
    [tournament],
  );

  // Each player's group, so we can show the pool label and enforce the same
  // "a group must keep at least two players" rule the server does.
  const groupByPlayer = useMemo(() => {
    const map: Record<string, { label: string; size: number }> = {};
    if (!tournament) return map;
    for (const group of Object.values(tournament.groups)) {
      for (const pid of group.playerIds) {
        map[pid] = { label: group.label, size: group.playerIds.length };
      }
    }
    return map;
  }, [tournament]);

  // Withdrawals are only meaningful while the tournament is running; a finished
  // one is locked and a draft edits its roster via the DraftRoster view instead.
  const canWithdraw = tournament?.status === 'IN_PROGRESS';

  const confirmWithdraw = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await withdrawPlayer(pending.id);
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  if (players.length === 0) return null;

  const isFootball = tournament?.sportId === 'FOOTBALL';
  const columns = [
    '64px minmax(140px, 1fr) minmax(0, 2fr)',
    isFootball ? '96px' : null,
    '72px',
    canWithdraw ? '56px' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {isFootball ? t('admin.players.teamsTitle') : t('admin.players.title')}
        </Typography>
        <Chip
          variant="outlined"
          label={
            isFootball
              ? t('admin.players.teamCount', { count: players.length })
              : t('admin.players.count', { count: players.length })
          }
        />
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: columns,
            columnGap: 2,
            rowGap: 1,
            alignItems: 'center',
          }}
        >
          <Head>{t('admin.players.table.seed')}</Head>
          <Head>
            {isFootball
              ? t('admin.players.table.team')
              : t('admin.players.table.name')}
          </Head>
          <Head>{t('admin.players.table.uniqueId')}</Head>
          {isFootball && <Head>{t('admin.players.table.members')}</Head>}
          <Head>{t('admin.players.table.group')}</Head>
          {canWithdraw && <Head> </Head>}
          {players.map((player) => {
            const group = groupByPlayer[player.id];
            // Mirror the server guard: a group must keep at least two players.
            const lastTwo = (group?.size ?? 0) <= 2;
            return (
              <Box key={player.id} sx={{ display: 'contents' }}>
                <Typography variant="body2" color="text.secondary">
                  {player.id}
                </Typography>
                <Typography variant="body2" noWrap title={player.name} sx={{ minWidth: 0 }}>
                  {player.name}
                </Typography>
                <Tooltip title={player.playerKey} arrow>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ fontFamily: 'monospace', fontSize: 12, minWidth: 0 }}
                  >
                    {player.playerKey}
                  </Typography>
                </Tooltip>
                {isFootball && (
                  <Box>
                    <Tooltip
                      arrow
                      title={
                        player.members.length > 0
                          ? player.members.join(', ')
                          : t('admin.players.members.none')
                      }
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<GroupsOutlinedIcon />}
                        label={player.members.length}
                      />
                    </Tooltip>
                  </Box>
                )}
                <Box>
                  <Chip size="small" variant="outlined" label={group?.label ?? '—'} />
                </Box>
                {canWithdraw && (
                  <Box sx={{ textAlign: 'right' }}>
                    <Tooltip
                      title={
                        lastTwo
                          ? t('admin.players.withdraw.blockedTooltip')
                          : t('admin.players.withdraw.tooltip')
                      }
                      arrow
                    >
                      {/* span keeps the tooltip working while the button is disabled */}
                      <span>
                        <IconButton
                          aria-label={t('admin.players.withdraw.aria', {
                            id: player.id,
                          })}
                          size="small"
                          color="error"
                          disabled={lastTwo}
                          onClick={() => setPending(player)}
                        >
                          <PersonRemoveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>

      <Dialog open={pending !== null} onClose={() => !busy && setPending(null)}>
        <DialogTitle>
          {t('admin.players.withdraw.dialogTitle', { name: pending?.name ?? '' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('admin.players.withdraw.dialogBody')}
          </DialogContentText>
          <Alert severity="warning">
            {t('admin.players.withdraw.dialogWarning', {
              name: pending?.name ?? '',
            })}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={busy}>
            {t('admin.common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void confirmWithdraw()}
            disabled={busy}
          >
            {t('admin.players.withdraw.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Box>
      <Typography variant="h6">{value}</Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function Head({ children }: { children: ReactNode }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
      {children}
    </Typography>
  );
}
